import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { groupBy, map, size } from 'lodash';
import React from 'react';

import { LinkToCaseViewerComponent } from 'components/atoms/DataGridRenderers/CaseIdCellRenderer';
import { LinkToSlideViewerComponent } from 'components/atoms/DataGridRenderers/SlideIdCellRenderer';
import { CaseSlideThumbnailData } from 'interfaces/procedure';
import { NormalizationCoverageStatus } from './helpers';

export interface NormalizationCheckStatusProps {
  coverageStatus?: NormalizationCoverageStatus;
  isLoading?: boolean;
  isBlocking?: boolean;
  caseStudyId?: string;
  slideThumbnailDataBySlideId?: Record<string, CaseSlideThumbnailData>;
}

export const NormalizationCheckStatus: React.FC<NormalizationCheckStatusProps> = ({
  coverageStatus,
  isLoading = false,
  isBlocking = false,
  caseStudyId,
  slideThumbnailDataBySlideId,
}) => {
  const theme = useTheme();

  const tone = isLoading
    ? theme.palette.info
    : coverageStatus?.isComplete
    ? theme.palette.success
    : theme.palette.warning;
  const missingSlideGroups = map(
    groupBy(coverageStatus?.missingSlideIds || [], (slideId) => {
      const caseId = slideThumbnailDataBySlideId?.[slideId]?.caseId;
      return typeof caseId === 'number' ? `case-${caseId}` : `slide-${slideId}`;
    }),
    (slideIds) => {
      const firstSlideId = slideIds[0];
      const caseId = slideThumbnailDataBySlideId?.[firstSlideId]?.caseId;

      return {
        caseId: typeof caseId === 'number' ? caseId : undefined,
        slideIds,
      };
    }
  );

  const title = isLoading
    ? 'Normalization check'
    : coverageStatus?.isComplete
    ? 'Normalization check passed'
    : 'Normalization check failed';
  const totalSelectedSlidesCount = coverageStatus?.totalSelectedSlidesCount || 0;
  const missingSlidesCount = size(coverageStatus?.missingSlideIds || []);
  const message = isLoading
    ? 'Checking normalization values for the selected slides.'
    : !coverageStatus || totalSelectedSlidesCount === 0
    ? 'No selected slides are available for this check.'
    : coverageStatus.isComplete
    ? totalSelectedSlidesCount === 1
      ? 'Normalization values are available for the selected slide.'
      : `Normalization values are available on all ${totalSelectedSlidesCount} selected slides.`
    : missingSlidesCount === 1 && totalSelectedSlidesCount === 1
    ? 'Normalization values are missing on the selected slide.'
    : `Normalization values are missing on ${missingSlidesCount} of ${totalSelectedSlidesCount} selected slides.`;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.25,
        borderColor: tone.main,
        backgroundColor: alpha(tone.main, theme.palette.mode === 'dark' ? 0.18 : 0.08),
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          {isLoading ? (
            <CircularProgress size={18} sx={{ color: tone.main, mt: 0.25 }} />
          ) : coverageStatus?.isComplete ? (
            <CheckCircleOutlineIcon sx={{ color: tone.main, mt: 0.1 }} fontSize="small" />
          ) : (
            <ErrorOutlineIcon sx={{ color: tone.main, mt: 0.1 }} fontSize="small" />
          )}
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">{title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {message}
            </Typography>
          </Stack>
        </Stack>

        {!isLoading && coverageStatus && !coverageStatus.isComplete && size(coverageStatus.missingSlideIds) > 0 ? (
          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary">
              Open affected cases/slides:
            </Typography>
            {map(missingSlideGroups, (missingSlideGroup, groupIndex) => (
              <Stack
                key={`${missingSlideGroup.caseId || 'slides'}-${groupIndex}`}
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                {typeof missingSlideGroup.caseId === 'number' ? (
                  <LinkToCaseViewerComponent
                    caseId={missingSlideGroup.caseId}
                    caseStudyId={caseStudyId}
                    formattedValue={`Case ${missingSlideGroup.caseId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ) : null}
                {map(missingSlideGroup.slideIds, (slideId) => (
                  <LinkToSlideViewerComponent
                    key={slideId}
                    slideId={slideId}
                    caseStudyId={caseStudyId}
                    formattedValue={slideId}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ))}
              </Stack>
            ))}
            <Typography variant="caption" color="text.secondary">
              {isBlocking
                ? 'This run cannot proceed until normalization values are available for these slides.'
                : 'This run can still proceed, but results may be inconsistent on slides without normalization values.'}
            </Typography>
          </Stack>
        ) : null}
      </Stack>
    </Paper>
  );
};
